import React, { useState, useEffect } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Contributor } from "../../types/form";
import MultiSelect from "../MultiSelect/MultiSelect";
import Button from "../../../components/ui/Button";

interface AddContributorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (contributor: { name: string; roles: string; id?: string | number }) => void;
  contributor?: Contributor | null;
  mode?: "add" | "edit";
}

const AddContributorModal: React.FC<AddContributorModalProps> = ({
  isOpen,
  onClose,
  onSave,
  contributor,
  mode = "add",
}) => {
  const [name, setName] = useState("");
  const [roles, setRoles] = useState<string[]>([]);
  const [nameError, setNameError] = useState("");
  const [rolesError, setRolesError] = useState("");

  // Role options
  const roleOptions = [
    "Writer",
    "Producer",
    "Engineer",
    "Mixer",
    "Performer",
    "Vocalist",
    "Instrumentalist",
    "Arranger",
    "Composer",
    "Co-Writer",
    "Featured Artist",
    "Session Musician",
  ];

  // Populate form when contributor changes (edit mode)
  useEffect(() => {
    if (mode === "edit" && contributor) {
      setName(contributor.name);
      // Convert roles to array (handle both string and array formats)
      let rolesArray: string[] = [];
      if (typeof contributor.roles === 'string' && contributor.roles) {
        rolesArray = contributor.roles.split(',').map(r => r.trim()).filter(r => r.length > 0);
      } else if (Array.isArray(contributor.roles)) {
        rolesArray = contributor.roles;
      }
      setRoles(rolesArray);
      setNameError("");
      setRolesError("");
    } else if (mode === "add") {
      // Reset form for add mode
      setName("");
      setRoles([]);
      setNameError("");
      setRolesError("");
    }
  }, [contributor, mode, isOpen]);

  // Validation
  const validateName = (value: string) => {
    if (!value.trim()) {
      setNameError("Name is required");
      return false;
    }
    setNameError("");
    return true;
  };

  const validateRoles = (value: string[]) => {
    if (!value || value.length === 0) {
      setRolesError("At least one role is required");
      return false;
    }
    setRolesError("");
    return true;
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);
    if (value.trim()) {
      setNameError("");
    }
  };

  const handleRolesChange = (selectedRoles: string[]) => {
    setRoles(selectedRoles);
    if (selectedRoles.length > 0) {
      setRolesError("");
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const isNameValid = validateName(name);
    const isRolesValid = validateRoles(roles);

    if (!isNameValid || !isRolesValid) {
      return;
    }

    if (mode === "edit" && contributor) {
      onSave({
        name: name.trim(),
        roles: roles.join(', '),
        id: contributor.id
      });
    } else {
      onSave({ name: name.trim(), roles: roles.join(', ') });
    }

    // Reset form
    setName("");
    setRoles([]);
    setNameError("");
    setRolesError("");
    onClose();
  };

  const handleClose = () => {
    // Reset form on close
    setName("");
    setRoles([]);
    setNameError("");
    setRolesError("");
    onClose();
  };

  const isFormValid = name.trim() && roles.length > 0;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleClose}
    >
      <div
        className="relative w-full max-w-md p-6 bg-white rounded-lg shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl font-semibold text-neutral-900">
            {mode === "edit" ? "Edit Contributor" : "Add Contributor"}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="p-1 transition-colors rounded-md text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700 border border-transparent"
            aria-label="Close modal"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name Input */}
          <div className="space-y-2">
            <label htmlFor="contributor-name" className="block mb-2 text-sm font-medium text-neutral-700">
              Name <span className="text-danger-600">*</span>
            </label>
            <input
              type="text"
              id="contributor-name"
              value={name}
              onChange={handleNameChange}
              onBlur={() => validateName(name)}
              placeholder="Enter contributor name"
              className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 transition-colors ${
                nameError ? "border-danger-500" : "border-neutral-300"
              }`}
            />
            {nameError && (
              <p className="mt-1 text-xs text-danger-600">{nameError}</p>
            )}
          </div>

          {/* Role Select */}
          <div className="space-y-2">
            <label htmlFor="contributor-roles" className="block mb-2 text-sm font-medium text-neutral-700">
              Role <span className="text-danger-600">*</span>
            </label>
            <MultiSelect
              options={roleOptions}
              value={roles}
              onChange={handleRolesChange}
              placeholder="Select roles"
            />
            {rolesError && (
              <p className="mt-1 text-xs text-danger-600">{rolesError}</p>
            )}
            <p className="text-xs text-neutral-500">
              Select one or more roles from the dropdown
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="neutral" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSubmit} disabled={!isFormValid}>
              {mode === "edit" ? "Save Updates" : "Add Now"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddContributorModal;
